import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const journeySteps = [
  {
    step: "01",
    icon: "ri-file-text-line",
    weeks: "Today",
    badge: "Free",
    badgeIcon: "ri-gift-line",
    title: "Submit This Form",
    desc: "Fill in your current VMware or Nutanix environment details. Takes under 5 minutes. Our engineers use this to prepare your tailored assessment — no sales calls until you're ready.",
    bullets: ["Environment snapshot", "Workload & VM profile", "Licensing spend estimate"],
  },
  {
    step: "02",
    icon: "ri-search-eye-line",
    weeks: "Week 1–2",
    badge: "On-site or Remote",
    badgeIcon: "ri-map-pin-line",
    title: "Infrastructure Assessment",
    desc: "Our certified engineers audit your environment — cataloguing workloads, VM counts, storage usage, and licensing spend. We identify consolidation opportunities and map all dependencies.",
    bullets: ["VMware vSphere & Nutanix AHV inventory scan", "Licensing cost analysis vs Arcfra", "Risk & dependency mapping"],
  },
  {
    step: "03",
    icon: "ri-settings-3-line",
    weeks: "Week 3–6",
    badge: "Zero Downtime",
    badgeIcon: "ri-checkbox-circle-line",
    title: "Pilot Deployment & Validation",
    desc: "We deploy an AECP pilot (AVE + ABS) alongside your existing infrastructure. Non-critical workloads migrate first so your team can validate performance and compatibility — with full rollback available.",
    bullets: ["AECP pilot cluster setup (AVE + ABS + ANS)", "Live VM migration via Arcfra Migration Tools", "ExaGrid backup integration & testing"],
  },
  {
    step: "04",
    icon: "ri-rocket-line",
    weeks: "Week 7+",
    badge: "Local Support",
    badgeIcon: "ri-customer-service-2-line",
    title: "Full Cutover & Handover",
    desc: "Production workloads migrate in rolling batches with rollback safeguards at every stage. SE Distribution provides local post-migration support, firmware updates, and ongoing health monitoring.",
    bullets: ["Rolling production cutover plan", "IT team training on Arcfra", "Annual health-check programme"],
  },
];

export default function ArcfraAssessmentPage() {
  useSEO({
    title: "Free Arcfra VMware Migration Assessment Malaysia | TCO Comparison | SE Distribution",
    description: "Get a free VMware & Nutanix migration assessment from SE Distribution — TCO comparison, migration roadmap, and Arcfra architecture recommendation. Results in 5 business days. Malaysia-based engineers.",
    canonical: "https://www.sedistribution.com.my/arcfra-assessment",
    keywords: "VMware migration assessment Malaysia, Arcfra assessment, VMware alternative assessment, Nutanix migration Malaysia, HCI migration assessment, free IT assessment Malaysia",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea[name='additional_context']") as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setSubmitting(true);
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value as string);
    });
    try {
      await fetch("https://readdy.ai/api/form/d7g830aispognpoj2p4g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[340px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20enterprise%20data%20center%20server%20room%20with%20rows%20of%20high%20performance%20storage%20racks%20glowing%20LED%20lights%20cool%20blue%20white%20tones%20dramatic%20wide%20angle%20perspective%20ultra%20clean%20professional%20architecture%20photography%20no%20people&width=1400&height=600&seq=arcfra-assess-hero&orientation=landscape"
          alt="Arcfra Migration Assessment"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-20 max-w-7xl mx-auto w-full">
          <nav className="flex items-center gap-2 text-xs text-white/50 mb-5">
            <Link to="/" className="hover:text-white/80 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/solutions/data-protection" className="hover:text-white/80 transition-colors">HCI &amp; Storage</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/80">Migration Assessment</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4 w-fit">
            <i className="ri-exchange-line text-white text-xs"></i>
            <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">Step 1 of Your Migration Path</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-3 max-w-2xl">
            Free Arcfra Migration Assessment
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
            Tell us about your current VMware or Nutanix environment. Our certified engineers will prepare a tailored migration roadmap and TCO comparison — at no cost.
          </p>
        </div>
      </section>

      {/* Value Props Bar */}
      <section className="bg-[#1a1a1a] py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { icon: "ri-award-line", label: "Gartner-Recognized HCI Vendor 2025" },
            { icon: "ri-time-line", label: "Assessment in 5 Business Days" },
            { icon: "ri-shield-check-line", label: "No Vendor Lock-In (Open X86)" },
            { icon: "ri-customer-service-2-line", label: "1,000+ Enterprise Customers Proved" },
          ].map((item) => (
            <div key={item.label} className="flex flex-col items-center gap-1.5">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${item.icon} text-white text-lg`}></i>
              </div>
              <p className="text-xs text-white/60 font-medium">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline context banner */}
      <div className="bg-[#f0ede8] border-b border-[#1a1a1a]/8 px-6 lg:px-20 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/8 rounded-lg flex-shrink-0">
              <i className="ri-route-line text-[#1a1a1a] text-sm"></i>
            </div>
            <p className="text-sm text-[#1a1a1a]">
              <strong>This is Step 1 of 4</strong> in your migration journey —
              <span className="text-gray-500"> from assessment to full production cutover in as little as 6–10 weeks.</span>
            </p>
          </div>
          <Link
            to="/solutions/data-protection#migration"
            className="text-xs font-semibold text-[#1a1a1a] underline underline-offset-2 whitespace-nowrap hover:opacity-70 transition-opacity"
          >
            View full migration path <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16 lg:py-24 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-14">

          {/* Left: Info */}
          <div className="lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">What You Get</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] leading-tight mb-5">
              A Detailed Migration Roadmap — Tailored to Your Environment
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-8">
              With Broadcom’s acquisition of VMware driving licence costs up significantly, and Nutanix renewals becoming increasingly expensive, many Malaysian enterprises are actively evaluating alternatives. Arcfra AECP is Gartner-recognized (March &amp; April 2025) and trusted by 1,000+ enterprises — delivering the same enterprise-grade HCI capabilities with subscription licensing by CPU core, no per-socket surprises, and full compatibility with commodity X86 hardware.
            </p>

            <div className="space-y-5 mb-10">
              {[
                {
                  icon: "ri-file-chart-line",
                  title: "TCO Comparison Report",
                  desc: "Side-by-side cost analysis: your current VMware/Nutanix spend vs. Arcfra over 3 years.",
                },
                {
                  icon: "ri-map-2-line",
                  title: "Migration Roadmap",
                  desc: "Phased migration plan with timeline, risk assessment, and zero-downtime strategy.",
                },
                {
                  icon: "ri-cpu-line",
                  title: "Architecture Recommendation",
                  desc: "Right-sized Arcfra configuration based on your workload profile and growth projections.",
                },
                {
                  icon: "ri-shield-flash-line",
                  title: "Risk & Compatibility Analysis",
                  desc: "Workload compatibility check and risk mitigation plan for your specific environment.",
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-4 bg-[#f8f7f4] rounded-lg">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0">
                    <i className={`${item.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1a1a1a] text-sm mb-0.5">{item.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparison Teaser */}
            <div className="bg-[#1a1a1a] rounded-lg p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Quick Comparison</p>
              <div className="space-y-3">
                {[
                  { label: "Virtualisation", arcfra: "AVE — 600+ enterprise customers", vmware: "ESXi per-socket (Broadcom)" },
                  { label: "Block Storage", arcfra: "ABS — no separate license", vmware: "vSAN — extra license" },
                  { label: "Kubernetes", arcfra: "AKE — CNCF conformant, built-in", vmware: "Tanzu — separate product" },
                  { label: "Network & Security", arcfra: "ANS — microsegmentation built-in", vmware: "NSX — separate product" },
                  { label: "File Storage", arcfra: "AFS — no extra hardware cost", vmware: "Not included (3rd-party NAS)" },
                  { label: "AI Workloads", arcfra: "Neutree — private MaaS platform", vmware: "No native AI orchestration" },
                  { label: "Licensing", arcfra: "Subscription by CPU core", vmware: "Per-socket post-Broadcom" },
                  { label: "Analyst", arcfra: "Gartner ×2 2025 + DCIG Top 5", vmware: "Legacy incumbent" },
                ].map((row) => (
                  <div key={row.label} className="grid grid-cols-3 gap-2 text-xs">
                    <span className="text-white/40">{row.label}</span>
                    <span className="text-white font-medium">{row.arcfra}</span>
                    <span className="text-white/40 line-through">{row.vmware}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[500px] text-center px-8">
                <div className="w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-full mb-5">
                  <i className="ri-check-line text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-3">Assessment Request Received</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-md mb-4">
                  Thank you. Our certified Arcfra engineers will review your environment details and reach out within <strong>5 business days</strong> with your tailored migration assessment.
                </p>
                {/* Journey progress */}
                <div className="w-full max-w-sm bg-[#f8f7f4] rounded-xl p-5 mb-8 text-left">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Your migration journey</p>
                  <div className="space-y-3">
                    {[
                      { step: "01", label: "Submit Form", done: true },
                      { step: "02", label: "Infrastructure Assessment (Week 1–2)", done: false },
                      { step: "03", label: "Pilot Deployment & Validation (Week 3–6)", done: false },
                      { step: "04", label: "Full Cutover & Handover (Week 7+)", done: false },
                    ].map((s) => (
                      <div key={s.step} className="flex items-center gap-3">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-bold ${s.done ? "bg-[#1a1a1a] text-white" : "bg-gray-200 text-gray-400"}`}>
                          {s.done ? <i className="ri-check-line text-xs"></i> : s.step}
                        </div>
                        <span className={`text-xs ${s.done ? "text-[#1a1a1a] font-semibold" : "text-gray-400"}`}>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/arcfra-spotlight"
                    className="bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
                  >
                    Explore Arcfra Platform <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/solutions/data-protection"
                    className="border border-gray-200 text-[#1a1a1a] text-sm font-medium px-6 py-3 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Back to HCI &amp; Storage
                  </Link>
                </div>
              </div>
            ) : (
              <div className="bg-[#f8f7f4] rounded-xl p-8 lg:p-10">
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-1">Environment Assessment Form</h3>
                <p className="text-sm text-gray-500 mb-8">All fields marked * are required. We treat your information with strict confidentiality.</p>

                <form
                  data-readdy-form
                  id="arcfra-migration-assessment"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Contact Info */}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Contact Information</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="full_name"
                          required
                          placeholder="Ahmad bin Abdullah"
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Job Title *</label>
                        <input
                          type="text"
                          name="job_title"
                          required
                          placeholder="IT Manager / CTO / System Admin"
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="you@company.com.my"
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="+60 12-xxx xxxx"
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Company Name *</label>
                        <input
                          type="text"
                          name="company_name"
                          required
                          placeholder="Your Company Sdn Bhd"
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Current Environment */}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Current Virtualisation Environment</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Current Platform *</label>
                        <select
                          name="current_platform"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select platform</option>
                          <option value="VMware vSphere / vCenter">VMware vSphere / vCenter</option>
                          <option value="VMware Cloud Foundation (VCF)">VMware Cloud Foundation (VCF)</option>
                          <option value="Nutanix AOS / AHV">Nutanix AOS / AHV</option>
                          <option value="Nutanix Cloud Platform">Nutanix Cloud Platform</option>
                          <option value="Microsoft Hyper-V">Microsoft Hyper-V</option>
                          <option value="Mixed VMware and Nutanix">Mixed VMware and Nutanix</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Number of Physical Hosts *</label>
                        <select
                          name="host_count"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select range</option>
                          <option value="1-5 hosts">1–5 hosts</option>
                          <option value="6-20 hosts">6–20 hosts</option>
                          <option value="21-50 hosts">21–50 hosts</option>
                          <option value="51-100 hosts">51–100 hosts</option>
                          <option value="100+ hosts">100+ hosts</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Number of Virtual Machines *</label>
                        <select
                          name="vm_count"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select range</option>
                          <option value="Under 50 VMs">Under 50 VMs</option>
                          <option value="50-200 VMs">50–200 VMs</option>
                          <option value="201-500 VMs">201–500 VMs</option>
                          <option value="501-1000 VMs">501–1,000 VMs</option>
                          <option value="1000+ VMs">1,000+ VMs</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Total Storage Capacity *</label>
                        <select
                          name="storage_capacity"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select range</option>
                          <option value="Under 10TB">Under 10TB</option>
                          <option value="10-50TB">10–50TB</option>
                          <option value="51-200TB">51–200TB</option>
                          <option value="201-500TB">201–500TB</option>
                          <option value="500TB+">500TB+</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Current Annual Licence Cost (RM) *</label>
                        <select
                          name="annual_licence_cost"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select range</option>
                          <option value="Under RM 50,000">Under RM 50,000</option>
                          <option value="RM 50,000 - RM 200,000">RM 50,000 – RM 200,000</option>
                          <option value="RM 200,000 - RM 500,000">RM 200,000 – RM 500,000</option>
                          <option value="RM 500,000 - RM 1,000,000">RM 500,000 – RM 1,000,000</option>
                          <option value="Above RM 1,000,000">Above RM 1,000,000</option>
                          <option value="Not sure">Not sure</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Licence Renewal Timeline *</label>
                        <select
                          name="renewal_timeline"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select timeline</option>
                          <option value="Within 3 months">Within 3 months</option>
                          <option value="3-6 months">3–6 months</option>
                          <option value="6-12 months">6–12 months</option>
                          <option value="12-24 months">12–24 months</option>
                          <option value="Already renewed / exploring proactively">Already renewed / exploring proactively</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Workloads */}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Workload Profile</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Primary Workloads *</label>
                        <select
                          name="primary_workloads"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select primary workload</option>
                          <option value="Enterprise Applications (ERP, CRM)">Enterprise Applications (ERP, CRM)</option>
                          <option value="Databases (SQL, Oracle, PostgreSQL)">Databases (SQL, Oracle, PostgreSQL)</option>
                          <option value="Virtual Desktop Infrastructure (VDI)">Virtual Desktop Infrastructure (VDI)</option>
                          <option value="File & Collaboration Services">File &amp; Collaboration Services</option>
                          <option value="AI / ML / Analytics">AI / ML / Analytics</option>
                          <option value="Mixed workloads">Mixed workloads</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Industry Sector *</label>
                        <select
                          name="industry_sector"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select industry</option>
                          <option value="Financial Services / Banking">Financial Services / Banking</option>
                          <option value="Healthcare">Healthcare</option>
                          <option value="Government / Public Sector">Government / Public Sector</option>
                          <option value="Manufacturing">Manufacturing</option>
                          <option value="Logistics & Supply Chain">Logistics &amp; Supply Chain</option>
                          <option value="Retail & E-commerce">Retail &amp; E-commerce</option>
                          <option value="Education">Education</option>
                          <option value="Oil & Gas / Energy">Oil &amp; Gas / Energy</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Downtime Tolerance *</label>
                        <select
                          name="downtime_tolerance"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select tolerance</option>
                          <option value="Zero tolerance (mission-critical 24/7)">Zero tolerance (mission-critical 24/7)</option>
                          <option value="Minimal (under 1 hour per month)">Minimal (under 1 hour per month)</option>
                          <option value="Moderate (planned maintenance windows OK)">Moderate (planned maintenance windows OK)</option>
                          <option value="Flexible (non-critical systems)">Flexible (non-critical systems)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">Migration Urgency *</label>
                        <select
                          name="migration_urgency"
                          required
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer"
                        >
                          <option value="">Select urgency</option>
                          <option value="Urgent - need to migrate before next renewal">Urgent – need to migrate before next renewal</option>
                          <option value="Active evaluation - comparing options now">Active evaluation – comparing options now</option>
                          <option value="Planning ahead - 12+ months horizon">Planning ahead – 12+ months horizon</option>
                          <option value="Just exploring - no firm timeline">Just exploring – no firm timeline</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Additional Context */}
                  <div>
                    <label className="block text-xs font-medium text-[#1a1a1a] mb-1.5">
                      Additional Context <span className="text-gray-400 font-normal">(optional, max 500 characters)</span>
                    </label>
                    <textarea
                      name="additional_context"
                      rows={4}
                      maxLength={500}
                      placeholder="Describe any specific challenges, compliance requirements, or questions you have about migrating from VMware or Nutanix..."
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1a1a1a] transition-colors resize-none"
                      onChange={(e) => setCharCount(e.target.value.length)}
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{charCount}/500</p>
                  </div>

                  <p className="text-xs text-gray-400 leading-relaxed">
                    By submitting this form, you agree that SE Distribution Sdn Bhd may contact you regarding your assessment request. Your information will not be shared with third parties.
                  </p>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#1a1a1a] text-white text-sm font-semibold py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Request My Free Assessment
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Full Migration Journey */}
      <section className="py-16 px-6 lg:px-20 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">What Happens Next</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-2">Your Full Migration Journey</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">End-to-end from this assessment form to a fully live Arcfra environment — managed by SE Distribution at every stage.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {journeySteps.map((s, idx) => (
              <div key={s.step} className={`relative rounded-xl p-6 border flex flex-col ${idx === 0 ? "bg-[#1a1a1a] border-[#1a1a1a]" : "bg-white border-gray-100"}`}>
                {/* Active indicator */}
                {idx === 0 && (
                  <div className="absolute -top-2.5 left-6">
                    <span className="bg-white text-[#1a1a1a] text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border border-gray-200">
                      You are here
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3 mb-4 mt-1">
                  <span className={`text-3xl font-black leading-none select-none ${idx === 0 ? "text-white/20" : "text-[#1a1a1a]/10"}`}>{s.step}</span>
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${idx === 0 ? "bg-white/15" : "bg-[#1a1a1a]"}`}>
                    <i className={`${s.icon} text-base ${idx === 0 ? "text-white" : "text-white"}`}></i>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 mb-2 w-fit" style={{ background: idx === 0 ? "rgba(255,255,255,0.12)" : "#f0ede8" }}>
                  <i className={`${s.badgeIcon} text-xs ${idx === 0 ? "text-white/70" : "text-[#1a1a1a]"}`}></i>
                  <span className={`text-[9px] font-bold uppercase tracking-widest ${idx === 0 ? "text-white/70" : "text-[#1a1a1a]"}`}>{s.badge}</span>
                </div>
                <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${idx === 0 ? "text-white/40" : "text-gray-400"}`}>{s.weeks}</p>
                <h4 className={`font-bold text-sm mb-2 ${idx === 0 ? "text-white" : "text-[#1a1a1a]"}`}>{s.title}</h4>
                <ul className="space-y-1.5 mt-auto">
                  {s.bullets.map((b) => (
                    <li key={b} className={`flex items-start gap-1.5 text-xs ${idx === 0 ? "text-white/60" : "text-gray-500"}`}>
                      <i className={`ri-checkbox-circle-fill mt-0.5 flex-shrink-0 ${idx === 0 ? "text-white/40" : "text-[#1a1a1a]/40"}`}></i>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Timeline bar */}
          <div className="bg-white rounded-xl border border-gray-100 px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg">
                <i className="ri-calendar-check-line text-[#1a1a1a] text-base"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-[#1a1a1a]">Typical end-to-end timeline: 6–10 weeks</p>
                <p className="text-xs text-gray-400">Varies by workload complexity and cluster size</p>
              </div>
            </div>
            <Link
              to="/solutions/data-protection"
              className="text-sm font-semibold text-[#1a1a1a] border border-[#1a1a1a]/20 px-5 py-2.5 rounded-md hover:bg-[#f8f7f4] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              View full migration path <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1a1a1a] py-16 px-6 lg:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Have Questions Before Submitting?</h2>
          <p className="text-white/60 text-sm mb-7 max-w-lg mx-auto">
            Our team is happy to answer any questions about the assessment process or Arcfra&apos;s capabilities before you commit to anything.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <a
              href="tel:+60364127917"
              className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
            >
              <i className="ri-phone-line"></i>
              +603 6412 7917
            </a>
            <a
              href="mailto:sales@sedistribution.com.my"
              className="border border-white/40 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
            >
              <i className="ri-mail-line"></i>
              sales@sedistribution.com.my
            </a>
          </div>
          {/* VMware Alternative Malaysia cross-link */}
          <div className="border-t border-white/10 pt-8">
            <p className="text-xs text-white/40 mb-4">Researching pricing &amp; TCO before booking? See the full comparison first:</p>
            <Link
              to="/vmware-alternative-malaysia"
              className="inline-flex items-center gap-3 bg-white/5 border border-white/15 rounded-xl px-6 py-4 hover:bg-white/10 hover:border-white/25 transition-all cursor-pointer group"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                <i className="ri-exchange-line text-white text-base"></i>
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-white">VMware Alternative Malaysia</p>
                <p className="text-xs text-white/45">Pricing, TCO savings, head-to-head comparison &amp; customer results</p>
              </div>
              <i className="ri-arrow-right-line text-white/40 group-hover:text-white/70 transition-colors ml-auto"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
